import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  FolderArchive, Plus, Search, Pencil, Trash2, Loader2,
  UserCircle, Clock, MessageSquare, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  "France", "Bénin", "Côte d'Ivoire", "Sénégal", "Togo", "Cameroun", "Mali",
  "Burkina Faso", "Niger", "Gabon", "Congo (RDC)", "Congo (Brazzaville)", "Guinée",
  "Tchad", "OHADA (espace)", "Belgique", "Suisse", "Luxembourg", "Canada (Québec)",
  "Maroc", "Tunisie", "Algérie", "Madagascar", "Autre"
];

const statusColors = {
  ouvert: "bg-green-100 text-green-700",
  en_cours: "bg-blue-100 text-blue-700",
  suspendu: "bg-amber-100 text-amber-700",
  ferme: "bg-slate-100 text-slate-600"
};
const statusLabels = {
  ouvert: "Ouvert", en_cours: "En cours", suspendu: "Suspendu", ferme: "Fermé"
};
const priorityColors = {
  faible: "bg-slate-100 text-slate-600",
  normal: "bg-blue-100 text-blue-700",
  urgent: "bg-red-100 text-red-700"
};
const typeLabels = {
  civil: "Civil", commercial: "Commercial", penal: "Pénal", social: "Social", administratif: "Administratif"
};

function CaseForm({ caseData, clients, onSave, onClose }) {
  const [form, setForm] = useState({
    title: caseData?.title || "",
    description: caseData?.description || "",
    client_id: caseData?.client_id || "",
    case_type: caseData?.case_type || "civil",
    priority: caseData?.priority || "normal",
    status: caseData?.status || "ouvert",
    country: caseData?.country || "",
    plaintiff: caseData?.plaintiff || "",
    defendant: caseData?.defendant || ""
  });
  const [saving, setSaving] = useState(false);

  function handleChange(field, value) {
    setForm(function(prev) { return { ...prev, [field]: value }; });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      const url = caseData?.case_id
        ? API_URL + "/api/cases/" + caseData.case_id
        : API_URL + "/api/cases";
      const method = caseData?.case_id ? "PUT" : "POST";
      const body = { ...form };
      if (!body.client_id) delete body.client_id;
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(caseData?.case_id ? "Dossier modifié" : "Dossier créé");
        onSave(); onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de connexion"); }
    setSaving(false);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Titre du dossier</Label>
          <Input value={form.title} onChange={function(e) { handleChange("title", e.target.value); }} data-testid="case-title-input" />
        </div>
        <div>
          <Label>Type d'affaire</Label>
          <Select value={form.case_type} onValueChange={function(v) { handleChange("case_type", v); }}>
            <SelectTrigger data-testid="case-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="civil">Civil</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="penal">Pénal</SelectItem>
              <SelectItem value="social">Social</SelectItem>
              <SelectItem value="administratif">Administratif</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Priorité</Label>
          <Select value={form.priority} onValueChange={function(v) { handleChange("priority", v); }}>
            <SelectTrigger data-testid="case-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="faible">Faible</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label>Client associé</Label>
          <Select value={form.client_id || "none"} onValueChange={function(v) { handleChange("client_id", v === "none" ? "" : v); }}>
            <SelectTrigger data-testid="case-client-select"><SelectValue placeholder="Sélectionner un client..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              {clients.map(function(c) {
                return <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Pays / droit applicable</Label>
          <Select value={form.country || "none"} onValueChange={function(v) { handleChange("country", v === "none" ? "" : v); }}>
            <SelectTrigger data-testid="case-country-select"><SelectValue placeholder="Choisir un pays..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Non précisé</SelectItem>
              {COUNTRIES.map(function(c) { return <SelectItem key={c} value={c}>{c}</SelectItem>; })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Demandeur</Label>
          <Input value={form.plaintiff} onChange={function(e) { handleChange("plaintiff", e.target.value); }} data-testid="case-plaintiff-input" />
        </div>
        <div className="col-span-2">
          <Label>Défendeur</Label>
          <Input value={form.defendant} onChange={function(e) { handleChange("defendant", e.target.value); }} data-testid="case-defendant-input" />
        </div>
        {caseData?.case_id && (
          <div>
            <Label>Statut</Label>
            <Select value={form.status} onValueChange={function(v) { handleChange("status", v); }}>
              <SelectTrigger data-testid="case-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ouvert">Ouvert</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="suspendu">Suspendu</SelectItem>
                <SelectItem value="ferme">Fermé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="col-span-2">
          <Label>Description</Label>
          <Textarea value={form.description} onChange={function(e) { handleChange("description", e.target.value); }} rows={3} data-testid="case-description-input" />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" disabled={saving} data-testid="save-case-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {caseData?.case_id ? "Modifier" : "Créer"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function CaseDetail({ caseData, onRefresh }) {
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  async function handleAddNote() {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const res = await fetch(API_URL + "/api/cases/" + caseData.case_id + "/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: noteText })
      });
      if (res.ok) { toast.success("Note ajoutée"); setNoteText(""); onRefresh(); }
    } catch (e) { toast.error("Erreur"); }
    setAddingNote(false);
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Référence</p>
          <p className="text-sm font-mono font-medium">{caseData.reference}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Statut</p>
          <Badge className={statusColors[caseData.status]}>{statusLabels[caseData.status]}</Badge>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Type</p>
          <p className="text-sm">{typeLabels[caseData.case_type] || caseData.case_type}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Priorité</p>
          <Badge className={priorityColors[caseData.priority]}>{caseData.priority}</Badge>
        </div>
        {caseData.client_name && (
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">Client</p>
            <p className="text-sm flex items-center gap-1"><UserCircle className="w-3 h-3" />{caseData.client_name}</p>
          </div>
        )}
      </div>
      {caseData.description && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Description</p>
          <p className="text-sm bg-slate-50 p-3 rounded-md">{caseData.description}</p>
        </div>
      )}

      <div>
        <p className="text-xs text-muted-foreground mb-2">Notes ({(caseData.notes || []).length})</p>
        <div className="space-y-2 mb-3">
          {(caseData.notes || []).map(function(n) {
            return (
              <div key={n.note_id} className="p-3 bg-slate-50 rounded-md border border-slate-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">{n.author}</span>
                  <span className="text-xs text-muted-foreground">{new Date(n.timestamp).toLocaleString("fr-FR")}</span>
                </div>
                <p className="text-sm">{n.content}</p>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Textarea
            value={noteText}
            onChange={function(e) { setNoteText(e.target.value); }}
            placeholder="Ajouter une note..."
            rows={2}
            className="flex-1"
            data-testid="case-note-input"
          />
          <Button size="sm" onClick={handleAddNote} disabled={addingNote || !noteText.trim()} className="self-end" data-testid="add-note-btn">
            {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Cases({ user, setUser }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingCase, setEditingCase] = useState(null);

  useEffect(function() {
    fetchCases();
    fetch(API_URL + "/api/clients", { credentials: "include" })
      .then(function(r) { return r.json(); })
      .then(setClients)
      .catch(function() {});
  }, []);

  async function fetchCases() {
    try {
      const url = API_URL + "/api/cases" + (filterStatus ? "?status=" + filterStatus : "");
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) setCases(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }

  useEffect(function() { fetchCases(); }, [filterStatus]);

  async function handleDelete(caseId) {
    if (!window.confirm("Supprimer ce dossier ?")) return;
    try {
      const res = await fetch(API_URL + "/api/cases/" + caseId, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Dossier supprimé"); fetchCases(); }
    } catch (e) { toast.error("Erreur"); }
  }

  const filtered = cases.filter(function(c) {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.title.toLowerCase().includes(q) || c.reference.toLowerCase().includes(q) || (c.client_name || "").toLowerCase().includes(q);
  });

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="cases-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Dossiers</h1>
            <p className="text-sm text-muted-foreground">
              {cases.length} dossier{cases.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Dialog open={showForm} onOpenChange={function(open) { if (!open) setEditingCase(null); setShowForm(open); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-case-btn" onClick={function() { setEditingCase(null); setShowForm(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Nouveau dossier
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingCase ? "Modifier le dossier" : "Nouveau dossier"}</DialogTitle>
                <DialogDescription>Remplissez les informations du dossier</DialogDescription>
              </DialogHeader>
              <CaseForm caseData={editingCase} clients={clients} onSave={fetchCases} onClose={function() { setShowForm(false); setEditingCase(null); }} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} placeholder="Rechercher un dossier..." className="pl-9" data-testid="search-cases" />
          </div>
          <Select value={filterStatus || "all"} onValueChange={function(v) { setFilterStatus(v === "all" ? "" : v); }}>
            <SelectTrigger className="w-40" data-testid="filter-status">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="ouvert">Ouvert</SelectItem>
              <SelectItem value="en_cours">En cours</SelectItem>
              <SelectItem value="suspendu">Suspendu</SelectItem>
              <SelectItem value="ferme">Fermé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FolderArchive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-1">{searchQuery ? "Aucun résultat" : "Aucun dossier"}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchQuery ? "Essayez une autre recherche" : "Créez votre premier dossier"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filtered.map(function(c) {
              return (
                <div
                  key={c.case_id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-blue-200 bg-white transition-colors"
                  data-testid={"case-" + c.case_id}
                >
                  <div className={"w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 " + (statusColors[c.status] || "bg-slate-100")}>
                    <FolderArchive className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={function() { navigate("/cases/" + c.case_id); }}>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{c.title}</p>
                      <span className="text-xs font-mono text-muted-foreground">{c.reference}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <Badge className={statusColors[c.status] + " text-xs"}>{statusLabels[c.status]}</Badge>
                      <span>{typeLabels[c.case_type] || c.case_type}</span>
                      {c.client_name && <span className="flex items-center gap-1"><UserCircle className="w-3 h-3" />{c.client_name}</span>}
                      <Badge className={priorityColors[c.priority] + " text-xs"}>{c.priority}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={function() { setEditingCase(c); setShowForm(true); }} data-testid={"edit-case-" + c.case_id}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={function() { handleDelete(c.case_id); }} data-testid={"delete-case-" + c.case_id}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
